/**
 * Feb 2026 — Passive PWA heartbeat hook.
 *
 * Fires `POST /api/telemetry/heartbeat` once per session with the detected
 * `display-mode: standalone` state. Backfills the `is_standalone` signal for
 * customers whose original `auth.login` events predate the telemetry
 * (collected before Phase 7-lite shipped) — drains the dispatcher's "awaiting
 * classification" cohort to ~0 within a few days of natural usage.
 *
 * Detection is overdetermined (3 sources) so any PWA installer is caught:
 *   • CSS media query `(display-mode: standalone)`  — Chrome/Edge/Android
 *   • `navigator.standalone === true`                — iOS Safari
 *   • CSS media query `(display-mode: fullscreen)`   — some Android PWAs
 *
 * Backend has a 12h dedup window so re-firing on every reload is safe.
 *
 * Auth-required (won't fire pre-login). UA-class is best-effort coarse
 * categorization useful for cohort analysis without raw UA storage.
 */
import { useEffect } from 'react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const SESSION_KEY = 'am_heartbeat_session_id';
const FIRED_KEY_PREFIX = 'am_heartbeat_fired_'; // per session

function detectStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    if (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true; // iOS Safari
  } catch {
    /* matchMedia not supported — assume browser */
  }
  return false;
}

function detectUaClass() {
  if (typeof window === 'undefined' || !window.navigator) return null;
  const ua = (window.navigator.userAgent || '').toLowerCase();
  if (/iphone|ipod/.test(ua)) return 'mobile_ios';
  if (/ipad/.test(ua)) return 'tablet_ios';
  if (/android/.test(ua)) return /mobile/.test(ua) ? 'mobile_android' : 'tablet_android';
  if (/macintosh|mac os x/.test(ua)) return 'desktop_mac';
  if (/windows/.test(ua)) return 'desktop_windows';
  if (/linux/.test(ua)) return 'desktop_linux';
  return 'unknown';
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function usePwaHeartbeat(isAuthed) {
  useEffect(() => {
    if (!isAuthed) return;
    const sessionId = getSessionId();
    if (!sessionId) return;
    const firedKey = FIRED_KEY_PREFIX + sessionId;
    try {
      if (sessionStorage.getItem(firedKey)) return; // already fired this session
    } catch { /* ignore */ }
    const payload = {
      is_standalone: detectStandalone(),
      ua_class: detectUaClass(),
      session_id: sessionId,
    };
    axios
      .post(`${API_URL}/api/telemetry/heartbeat`, payload, { withCredentials: true })
      .then(() => {
        try { sessionStorage.setItem(firedKey, '1'); } catch { /* ignore */ }
      })
      .catch(() => {
        // Telemetry must never break the app. Silent fail on network errors.
      });
  }, [isAuthed]);
}

export default usePwaHeartbeat;
